// Mints one shareable referral code per customer and emails it to them.
//
// The codes are ordinary DiscountCode records, so they appear in Products →
// Discounts with full redemption history alongside every hand-made code. The
// other half of the loop is server-side: when a friend orders with one of
// these, routes/discounts.js POST /redeem emails the owner their reward.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/auth';
import { Button, Input, Select, Textarea, Card, Pill } from '../lib/ui';

const label = { display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 5 };
const th = { textAlign: 'left', padding: '8px 12px', fontSize: 11, color: 'var(--muted)', borderBottom: '1px solid var(--border)', textTransform: 'uppercase', letterSpacing: '.04em' };
const td = { padding: '9px 12px', fontSize: 13, verticalAlign: 'middle' };

// Mirrors the enum on models/DiscountCode.js. needsValue drives whether the
// amount input is shown; free delivery has no magnitude to set.
const TERM_TYPES = {
  percent:             { label: '% off their order',   needsValue: true,  unit: '%' },
  fixed:               { label: '£ off their order',   needsValue: true,  unit: '£' },
  free_delivery:       { label: 'Free delivery',       needsValue: false },
  first_order_percent: { label: '% off (first order)', needsValue: true,  unit: '%' },
  first_order_fixed:   { label: '£ off (first order)', needsValue: true,  unit: '£' },
};

// The reward the code's owner earns. No first-order variants: by definition
// the owner has already ordered.
const REWARD_TYPES = {
  percent:       { label: '% off their next order', needsValue: true,  unit: '%' },
  fixed:         { label: '£ off their next order', needsValue: true,  unit: '£' },
  free_delivery: { label: 'Free delivery',          needsValue: false },
};

function describeOffer(type, value) {
  if (type === 'free_delivery') return 'free delivery';
  if (type?.includes('percent')) return `${Number(value) || 0}% off`;
  return `£${(Number(value) || 0).toFixed(2)} off`;
}

// The editable slots of the invite email. The layout, code box and branding
// are fixed server-side in helpers/referralEmails.js; these are just the words.
// Keys must match DEFAULT_INVITE_COPY there.
const COPY_FIELDS = [
  { key: 'subject',  label: 'Subject line',   rows: 1, hint: 'Shown in their inbox' },
  { key: 'headline', label: 'Headline',       rows: 1 },
  { key: 'intro',    label: 'Opening paragraph', rows: 3 },
  { key: 'earn',     label: 'What they earn', rows: 3, hint: 'Sits under the code box' },
  { key: 'cta',      label: 'Button text',    rows: 1 },
  { key: 'closing',  label: 'Closing note',   rows: 2, hint: 'Small print at the bottom' },
];

const PLACEHOLDERS = [
  ['{{name}}',   "the customer's first name"],
  ['{{code}}',   'their referral code'],
  ['{{offer}}',  'what their friends get, e.g. 15% off'],
  ['{{reward}}', 'what they earn, e.g. 10% off'],
];

export default function ReferralsTab({ isAdmin = true }) {
  // ── Offer terms ────────────────────────────────────────────────────────────
  const [termType,      setTermType]      = useState('first_order_percent');
  const [termValue,     setTermValue]     = useState('15');
  const [minOrderValue, setMinOrderValue] = useState('');
  const [expiresAt,     setExpiresAt]     = useState('');
  const [maxUses,       setMaxUses]       = useState('');

  const [rewardType,  setRewardType]  = useState('percent');
  const [rewardValue, setRewardValue] = useState('10');
  const [rewardDays,  setRewardDays]  = useState('90');

  // ── Recipients ─────────────────────────────────────────────────────────────
  const [days,        setDays]        = useState('60');
  const [customers,   setCustomers]   = useState(null);   // null = not loaded
  const [selected,    setSelected]    = useState(new Set());
  const [hideExisting, setHideExisting] = useState(false);
  const [loading,     setLoading]     = useState(false);

  // ── Email wording ──────────────────────────────────────────────────────────
  const [copy,        setCopy]        = useState(null);   // null = not loaded
  const [savedCopy,   setSavedCopy]   = useState(null);   // last persisted version
  const [showCopy,    setShowCopy]    = useState(false);
  const [savingCopy,  setSavingCopy]  = useState(false);

  // ── Existing codes ─────────────────────────────────────────────────────────
  const [codes,       setCodes]       = useState([]);
  const [showAll,     setShowAll]     = useState(false);

  // ── Send state ─────────────────────────────────────────────────────────────
  const [testEmail,   setTestEmail]   = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [sending,     setSending]     = useState(false);
  const [result,      setResult]      = useState(null);
  const [msg,         setMsg]         = useState('');

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 6000); };

  const loadCodes = useCallback(async () => {
    try {
      const { data } = await api.get('/email/referral-codes');
      setCodes(data);
    } catch (e) {
      flash(`Error loading codes: ${e.response?.data?.error || e.message}`);
    }
  }, []);

  const loadCopy = useCallback(async () => {
    try {
      const { data } = await api.get('/email/referral-copy');
      setCopy(data.copy);
      setSavedCopy(data.copy);
    } catch (e) {
      flash(`Error loading wording: ${e.response?.data?.error || e.message}`);
    }
  }, []);

  useEffect(() => { loadCodes(); loadCopy(); }, [loadCodes, loadCopy]);

  const copyDirty = useMemo(
    () => !!copy && !!savedCopy && COPY_FIELDS.some(f => (copy[f.key] || '') !== (savedCopy[f.key] || '')),
    [copy, savedCopy]
  );

  const saveCopy = async () => {
    setSavingCopy(true);
    try {
      const { data } = await api.put('/email/referral-copy', { copy });
      setCopy(data.copy);
      setSavedCopy(data.copy);
      flash('Wording saved');
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setSavingCopy(false);
    }
  };

  const resetCopy = async () => {
    if (!window.confirm('Discard your wording and go back to the default email?')) return;
    setSavingCopy(true);
    try {
      const { data } = await api.delete('/email/referral-copy');
      setCopy(data.copy);
      setSavedCopy(data.copy);
      flash('Reset to the default wording');
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setSavingCopy(false);
    }
  };

  // email → existing referral code, so the list can show who already has one
  const codeByEmail = useMemo(() => {
    const m = new Map();
    for (const c of codes) if (c.referrerEmail) m.set(c.referrerEmail, c);
    return m;
  }, [codes]);

  const loadCustomers = async () => {
    setLoading(true);
    setCustomers(null);
    setSelected(new Set());
    setResult(null);
    try {
      const { data } = await api.get('/email/recent-orders', { params: { days } });
      setCustomers(data);
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const visible = useMemo(() => {
    if (!customers) return [];
    return hideExisting ? customers.filter(c => !codeByEmail.has(c.email)) : customers;
  }, [customers, hideExisting, codeByEmail]);

  const toggle = (email) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(email)) next.delete(email); else next.add(email);
    return next;
  });

  const allVisibleSelected = visible.length > 0 && visible.every(c => selected.has(c.email));
  const toggleAll = () => setSelected(allVisibleSelected ? new Set() : new Set(visible.map(c => c.email)));

  // Shared by the test send and the real send so the preview is never stale.
  const payload = () => ({
    terms: {
      type:          termType,
      value:         TERM_TYPES[termType].needsValue ? Number(termValue) || 0 : 0,
      minOrderValue: minOrderValue === '' ? null : Number(minOrderValue),
      expiresAt:     expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : null,
      maxUses:       maxUses === '' ? null : Number(maxUses),
    },
    reward: {
      type:       rewardType,
      value:      REWARD_TYPES[rewardType].needsValue ? Number(rewardValue) || 0 : 0,
      expiryDays: Number(rewardDays) || 90,
    },
    // What you see in the editor is what sends, saved or not.
    ...(copy ? { copy } : {}),
  });

  const sendTest = async () => {
    if (!testEmail) return flash('Error: enter a test email address');
    setSendingTest(true);
    try {
      await api.post('/email/send-referral', { ...payload(), testEmail });
      flash(`Test sent to ${testEmail} (no code was created)`);
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setSendingTest(false);
    }
  };

  const send = async () => {
    if (!selected.size) return flash('Error: select at least one customer');

    const reissuing = [...selected].filter(e => codeByEmail.has(e)).length;
    const confirmMsg =
      `Email a referral code to ${selected.size} customer${selected.size === 1 ? '' : 's'}?\n\n` +
      `Their friends get: ${describeOffer(termType, termValue)}\n` +
      `They earn: ${describeOffer(rewardType, rewardValue)} per friend who orders\n\n` +
      (reissuing
        ? `${reissuing} already have a code. They keep the same code, but its terms are updated to match this send.`
        : 'All of these are new codes.') +
      (copyDirty ? '\n\nNote: your unsaved wording changes will be used for this send.' : '');
    if (!window.confirm(confirmMsg)) return;

    setSending(true);
    setResult(null);
    try {
      const recipients = [...selected].map(email => ({
        email,
        name: customers.find(c => c.email === email)?.name || '',
      }));
      const { data } = await api.post('/email/send-referral', { ...payload(), recipients });
      setResult(data);
      setSelected(new Set());
      await loadCodes();
      flash(`Done - ${data.sent} sent${data.failed ? `, ${data.failed} failed` : ''}`);
    } catch (e) {
      flash(`Error: ${e.response?.data?.error || e.message}`);
    } finally {
      setSending(false);
    }
  };

  const termNeedsValue   = TERM_TYPES[termType].needsValue;
  const rewardNeedsValue = REWARD_TYPES[rewardType].needsValue;

  return (
    <div>
      {msg && (
        <div style={{
          padding: '12px 16px', borderRadius: 'var(--r-lg)', marginBottom: 16, fontSize: 14,
          background: msg.startsWith('Error') ? '#fef2f2' : '#f0fdf4',
          border: `1px solid ${msg.startsWith('Error') ? '#fca5a5' : '#86efac'}`,
          color: msg.startsWith('Error') ? '#991b1b' : '#166534',
        }}>{msg}</div>
      )}

      {/* ── The offer ────────────────────────────────────────────────────── */}
      <Card style={{ padding: '20px 24px', marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16, color: 'var(--brand)' }}>The offer</h3>
        <p style={{ color: 'var(--muted)', fontSize: 14, margin: '0 0 18px' }}>
          Each customer gets their own code to share. Their friends get a discount; they earn a reward
          every time a friend orders with it.
        </p>

        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 10 }}>WHAT THEIR FRIENDS GET</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
          <div>
            <span style={label}>Discount</span>
            <Select value={termType} onChange={e => setTermType(e.target.value)}>
              {Object.entries(TERM_TYPES).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
            </Select>
          </div>
          {termNeedsValue && (
            <div>
              <span style={label}>Amount ({TERM_TYPES[termType].unit})</span>
              <Input type="number" min={0} value={termValue} onChange={e => setTermValue(e.target.value)} />
            </div>
          )}
          <div>
            <span style={label}>Min order £</span>
            <Input type="number" min={0} step="0.01" placeholder="none" value={minOrderValue} onChange={e => setMinOrderValue(e.target.value)} />
          </div>
          <div>
            <span style={label}>Max uses</span>
            <Input type="number" min={1} placeholder="unlimited" value={maxUses} onChange={e => setMaxUses(e.target.value)} />
          </div>
          <div>
            <span style={label}>Code expires</span>
            <Input type="date" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} />
          </div>
        </div>

        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 10 }}>WHAT THE CUSTOMER EARNS</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          <div>
            <span style={label}>Reward</span>
            <Select value={rewardType} onChange={e => setRewardType(e.target.value)}>
              {Object.entries(REWARD_TYPES).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
            </Select>
          </div>
          {rewardNeedsValue && (
            <div>
              <span style={label}>Amount ({REWARD_TYPES[rewardType].unit})</span>
              <Input type="number" min={0} value={rewardValue} onChange={e => setRewardValue(e.target.value)} />
            </div>
          )}
          <div>
            <span style={label}>Reward valid for (days)</span>
            <Input type="number" min={1} value={rewardDays} onChange={e => setRewardDays(e.target.value)} />
          </div>
        </div>

        <div style={{ marginTop: 16, padding: '10px 14px', background: 'var(--panel-2)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', fontSize: 13 }}>
          Friends get <strong>{describeOffer(termType, termValue)}</strong>; the customer gets{' '}
          <strong>{describeOffer(rewardType, rewardValue)}</strong> emailed to them for every friend who orders.
          A customer's own code never works on their own order.
        </div>
      </Card>

      {/* ── Email wording ────────────────────────────────────────────────── */}
      <Card style={{ padding: '20px 24px', marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, color: 'var(--brand)' }}>
              What the email says {copyDirty && <Pill tone="warn">Unsaved</Pill>}
            </h3>
            <p style={{ color: 'var(--muted)', fontSize: 13, margin: 0 }}>
              Edit the wording. The layout, code box and branding stay as they are.
            </p>
          </div>
          <Button onClick={() => setShowCopy(v => !v)}>{showCopy ? 'Hide' : 'Edit'}</Button>
        </div>

        {showCopy && (copy === null ? (
          <p style={{ color: 'var(--muted)', fontSize: 14, marginTop: 16 }}>Loading…</p>
        ) : (
          <div style={{ marginTop: 16 }}>
            <div style={{ padding: '10px 14px', background: 'var(--panel-2)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', fontSize: 12, marginBottom: 16 }}>
              <strong>Placeholders</strong> get filled in per customer:{' '}
              {PLACEHOLDERS.map(([token, meaning], i) => (
                <span key={token}>
                  {i > 0 && ', '}
                  <code style={{ fontWeight: 700 }}>{token}</code> ({meaning})
                </span>
              ))}
              . Leave a box empty to fall back to the default wording.
            </div>

            {COPY_FIELDS.map(f => (
              <div key={f.key} style={{ marginBottom: 14 }}>
                <span style={label}>
                  {f.label}
                  {f.hint && <span style={{ textTransform: 'none', fontWeight: 400, marginLeft: 6 }}>{f.hint}</span>}
                </span>
                {f.rows === 1 ? (
                  <Input
                    value={copy[f.key] || ''}
                    onChange={e => setCopy(p => ({ ...p, [f.key]: e.target.value }))}
                  />
                ) : (
                  <Textarea
                    rows={f.rows}
                    value={copy[f.key] || ''}
                    onChange={e => setCopy(p => ({ ...p, [f.key]: e.target.value }))}
                    style={{ width: '100%', resize: 'vertical' }}
                  />
                )}
              </div>
            ))}

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button variant="primary" onClick={saveCopy} disabled={savingCopy || !copyDirty}>
                {savingCopy ? 'Saving…' : 'Save wording'}
              </Button>
              {copyDirty && <Button onClick={() => setCopy(savedCopy)}>Discard changes</Button>}
              <Button onClick={resetCopy} disabled={savingCopy}>Reset to default</Button>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                Use the test send below to see it before it goes out.
              </span>
            </div>
          </div>
        ))}
      </Card>

      {/* ── Recipients ───────────────────────────────────────────────────── */}
      <Card style={{ padding: '20px 24px', marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 16, color: 'var(--brand)' }}>Who gets a code</h3>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', paddingBottom: 14, marginBottom: 14, borderBottom: '1px solid var(--border)' }}>
          <Input
            style={{ maxWidth: 280 }} type="email" placeholder="Send a test to yourself…"
            value={testEmail} onChange={e => setTestEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendTest()}
          />
          <Button onClick={sendTest} disabled={sendingTest}>
            {sendingTest ? 'Sending…' : 'Send test'}
          </Button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Uses a sample code and creates nothing.</span>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
          <span style={{ ...label, display: 'inline-block', marginBottom: 0 }}>Ordered in last</span>
          <Select
            style={{ width: 'auto' }} value={days}
            onChange={e => { setDays(e.target.value); setCustomers(null); setSelected(new Set()); }}
          >
            <option value="30">30 days</option>
            <option value="60">60 days</option>
            <option value="90">90 days</option>
            <option value="180">180 days</option>
          </Select>
          <Button onClick={loadCustomers} disabled={loading}>
            {loading ? 'Loading…' : 'Load customers'}
          </Button>
          {customers && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={hideExisting} onChange={e => setHideExisting(e.target.checked)} />
              Hide those who already have a code
            </label>
          )}
        </div>

        {customers !== null && (
          visible.length === 0 ? (
            <p style={{ color: 'var(--muted)', fontSize: 14 }}>
              {customers.length === 0 ? 'No orders found in this period.' : 'Everyone in this period already has a code.'}
            </p>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                  {visible.length} customer{visible.length !== 1 ? 's' : ''} &nbsp;·&nbsp; {selected.size} selected
                </span>
                <Button onClick={toggleAll}>{allVisibleSelected ? 'Deselect all' : 'Select all'}</Button>
              </div>

              <div style={{ maxHeight: 340, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', marginBottom: 14 }}>
                {visible.map(cust => {
                  const existing = codeByEmail.get(cust.email);
                  return (
                    <label
                      key={cust.email}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                        borderBottom: '1px solid var(--border)', cursor: 'pointer',
                        background: selected.has(cust.email) ? 'var(--panel-2)' : 'transparent',
                      }}
                    >
                      <input
                        type="checkbox" checked={selected.has(cust.email)}
                        onChange={() => toggle(cust.email)}
                        style={{ width: 16, height: 16, flexShrink: 0 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>
                          {cust.name || <span style={{ color: 'var(--muted)', fontWeight: 400 }}>No name</span>}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {cust.email}
                        </div>
                      </div>
                      {existing && (
                        <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--muted)', flexShrink: 0 }}>
                          {existing.code} · {existing.usedCount} use{existing.usedCount === 1 ? '' : 's'}
                        </span>
                      )}
                      <span style={{ fontSize: 11, color: 'var(--muted)', flexShrink: 0 }}>
                        {new Date(cust.lastOrderAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </span>
                    </label>
                  );
                })}
              </div>

              <Button variant="primary" onClick={send} disabled={!isAdmin || sending || !selected.size}>
                {sending ? 'Sending…' : `Send code to ${selected.size} customer${selected.size !== 1 ? 's' : ''}`}
              </Button>

              {result && (
                <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--panel-2)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', fontSize: 13 }}>
                  {result.sent} sent · {result.failed} failed
                  {result.skipped?.length > 0 && ` · ${result.skipped.length} skipped (opted out)`}
                  {result.results?.length > 0 && (
                    <details style={{ marginTop: 6 }}>
                      <summary style={{ cursor: 'pointer' }}>See codes issued</summary>
                      <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                        {result.results.map((r, i) => (
                          <li key={i}>
                            {r.email}: {r.ok ? `${r.code}${r.reused ? ' (existing code reused)' : ''}` : `failed - ${r.error}`}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </>
          )
        )}
      </Card>

      {/* ── Existing codes ───────────────────────────────────────────────── */}
      <Card style={{ padding: '20px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showAll ? 16 : 0 }}>
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, color: 'var(--brand)' }}>
              Referral codes in the wild ({codes.length})
            </h3>
            <p style={{ color: 'var(--muted)', fontSize: 13, margin: 0 }}>
              Also editable under Products → Discounts, with full redemption history.
            </p>
          </div>
          <Button onClick={() => setShowAll(v => !v)}>{showAll ? 'Hide' : 'Show'}</Button>
        </div>

        {showAll && (
          codes.length === 0 ? (
            <p style={{ color: 'var(--muted)', fontSize: 14 }}>No referral codes issued yet.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>Code</th>
                    <th style={th}>Owner</th>
                    <th style={th}>Friends get</th>
                    <th style={th}>Used</th>
                    <th style={th}>Rewards paid</th>
                    <th style={th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {codes.map(c => (
                    <tr key={c.code} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700 }}>{c.code}</td>
                      <td style={td}>
                        <div>{c.referrerName || <span style={{ color: 'var(--muted)' }}>Unknown</span>}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>{c.referrerEmail || 'no owner on record'}</div>
                      </td>
                      <td style={td}>{describeOffer(c.type, c.value)}</td>
                      <td style={td}>{c.usedCount}{c.maxUses != null ? ` / ${c.maxUses}` : ''}</td>
                      <td style={td}>{c.rewardsIssued || 0}</td>
                      <td style={td}>
                        {!c.enabled ? <Pill tone="bad">Disabled</Pill>
                          : c.expiresAt && new Date(c.expiresAt) < new Date() ? <Pill tone="warn">Expired</Pill>
                          : <Pill tone="ok">Active</Pill>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </Card>
    </div>
  );
}
